import * as React from 'react';
import PropTypes from 'prop-types';
import ConnectedThemeProvider from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider';
import CssBaseline from '@material-ui/core/CssBaseline';
import merge from 'lodash/merge';
import { createTheme } from '@material-ui/core';

// Weha shop styling extracted from shop screenshots: sharp corners, dark blue header, light gray secondary buttons
const primary = 'rgb(15, 30, 80)'; // #0f1e50 – header, headings, primary CTAs
const primaryDark = 'rgb(10, 20, 55)';
const primaryContrast = '#ffffff';
const pageBackground = '#f5f5f5';
const paperBackground = '#ffffff';
const inputBackground = '#ffffff'; // Shop search bar is white
const textPrimary = '#333333'; // Shop body text
const textSecondary = '#555555';
const textMuted = '#666666';
const borderLight = '#e8e8e8';
const borderDefault = '#e0e0e0';
const borderMedium = '#d0d0d0';
const fontFamily = '"Open Sans", "Helvetica Neue", Arial, sans-serif';
const borderRadius = 0; // Shop: all buttons and inputs sharp, no rounding
const headerBackground = primary; // Dark blue bar, white text/icons
const headerTitleColor = primaryContrast;
const headerIconColor = 'rgba(255,255,255,0.9)';
/** Breadcrumb / net-price accent (e.g. header toggle icon). */
export const subHeaderTan = '#D5C09E';
const buttonSecondaryBg = '#E0E0E0'; // "Anmelden für Preise" style: light gray
const buttonSecondaryBorder = '#d0d0d0';
const shadowButton = '0 1px 1px rgba(0,0,0,0.06)';
const shadowButtonHover = '0 1px 2px rgba(0,0,0,0.08)';
const shadowPaper = '0 1px 1px rgba(0,0,0,0.04)';
const shadowHeader = '0 1px 0 0 rgba(0,0,0,0.1)';

const customTheme = createTheme({
  palette: {
    primary: {
      main: primary,
      dark: primaryDark,
      contrastText: primaryContrast,
    },
    secondary: {
      main: textPrimary,
    },
    background: {
      default: pageBackground,
      paper: paperBackground,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
    },
    divider: borderDefault,
  },
  typography: {
    fontFamily,
    fontSize: 14,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: { fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.3 },
    h2: { fontWeight: 700, fontSize: '1.375rem', lineHeight: 1.3 },
    body1: { fontSize: 14, lineHeight: 1.5 },
    body2: { fontSize: 13, lineHeight: 1.5 },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 14,
    },
  },
  shape: {
    borderRadius,
  },
  shadows: [
    'none',
    shadowPaper,
    shadowButton,
    '0 2px 4px rgba(0,0,0,0.08)',
    '0 2px 6px rgba(0,0,0,0.08)',
    '0 4px 8px rgba(0,0,0,0.08)',
    '0 4px 10px rgba(0,0,0,0.08)',
    '0 6px 12px rgba(0,0,0,0.08)',
    '0 6px 14px rgba(0,0,0,0.08)',
    '0 8px 16px rgba(0,0,0,0.08)',
    '0 8px 18px rgba(0,0,0,0.08)',
    '0 10px 20px rgba(0,0,0,0.08)',
    '0 10px 22px rgba(0,0,0,0.08)',
    '0 12px 24px rgba(0,0,0,0.08)',
    '0 12px 26px rgba(0,0,0,0.08)',
    '0 14px 28px rgba(0,0,0,0.08)',
    '0 14px 30px rgba(0,0,0,0.08)',
    '0 16px 32px rgba(0,0,0,0.08)',
    '0 16px 34px rgba(0,0,0,0.08)',
    '0 18px 36px rgba(0,0,0,0.08)',
    '0 18px 38px rgba(0,0,0,0.08)',
    '0 20px 40px rgba(0,0,0,0.08)',
    '0 20px 42px rgba(0,0,0,0.08)',
    '0 22px 44px rgba(0,0,0,0.08)',
    '0 24px 48px rgba(0,0,0,0.08)',
  ],
});

const overrideTheme = (theme) => {
  theme.palette.primary = {
    main: primary,
    dark: primaryDark,
    contrastText: primaryContrast,
  };
  theme.palette.secondary = { main: textPrimary };
  theme.palette.background = { default: pageBackground, paper: paperBackground };
  theme.palette.text = { primary: textPrimary, secondary: textSecondary };
  theme.palette.divider = borderDefault;
  theme.typography = { ...theme.typography, ...customTheme.typography };
  theme.shape = { ...theme.shape, borderRadius };
  theme.shadows = customTheme.shadows;

  theme.weha = {
    primary,
    primaryDark,
    primaryContrast,
    pageBackground,
    paperBackground,
    inputBackground,
    textPrimary,
    textSecondary,
    textMuted,
    borderLight,
    borderDefault,
    borderMedium,
    borderRadius,
    shadowPaper,
    shadowButton,
    shadowButtonHover,
    shadowHeader,
    header: {
      backgroundColor: headerBackground,
      borderBottom: `1px solid rgba(0,0,0,0.1)`,
      titleColor: headerTitleColor,
      iconColor: headerIconColor,
    },
    buttonSecondary: {
      backgroundColor: buttonSecondaryBg,
      color: textPrimary,
      borderColor: buttonSecondaryBorder,
      fontWeight: 600,
      hoverBackground: '#d5d5d5',
    },
    subHeaderTan,
  };

  merge(theme.overrides, {
    // Global document layout + scrollbar hiding
    MuiCssBaseline: {
      '@global': {
        'html, body': {
          overflow: 'hidden',
          height: '100%',
          '-ms-overflow-style': 'none !important',
          scrollbarWidth: 'none !important',
          '&::-webkit-scrollbar': {
            display: 'none !important',
          },
        },
        '#root': {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
        [theme.breakpoints.down('md')]: {
          'html, body': {
            overflow: 'auto',
            height: 'auto',
            minHeight: '100%',
            '-ms-overflow-style': 'none !important',
            scrollbarWidth: 'none !important',
            '&::-webkit-scrollbar': {
              display: 'none !important',
            },
          },
          '#root': {
            position: 'relative',
            minHeight: '100%',
            top: 'auto',
            right: 'auto',
            bottom: 'auto',
            left: 'auto',
          },
        },
      },
    },
    // CustomDialog (Load/Save config) only – :has(.MuiDialogTitle-root) so SelectBackgroundDialog is unchanged
    MuiDialog: {
      root: {
        '& .MuiDialog-paper:has(> div .MuiDialogTitle-root)': {
          borderRadius,
          padding: 0,
          overflow: 'hidden',
          borderTop: 'none !important',
        },
        '& .MuiDialog-paper:has(> div .MuiDialogTitle-root) .MuiDialogTitle-root': {
          backgroundColor: primary,
          color: primaryContrast,
          padding: '16px 24px',
          margin: 0,
          flex: 1,
          fontSize: '1rem',
          fontWeight: 600,
          fontFamily,
          '& .MuiTypography-root': {
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            fontFamily: 'inherit',
          },
        },
        '& .MuiDialog-paper:has(> div .MuiDialogTitle-root) > div:first-child': {
          display: 'flex',
          alignItems: 'center',
          backgroundColor: primary,
          minHeight: 48,
          width: '100%',
          boxSizing: 'border-box',
        },
        '& .MuiDialog-paper:has(> div .MuiDialogTitle-root) > div:first-child .MuiIconButton-root': {
          color: primaryContrast,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
          },
        },
        '& .MuiDialog-paper:has(> div .MuiDialogTitle-root) .MuiDialogContent-root': {
          padding: 24,
        },
        '& .MuiDialog-paper:has(> div .MuiDialogTitle-root) .MuiDialogActions-root': {
          padding: '16px 24px 24px',
          '& .MuiButton-root': {
            minHeight: 40,
            minWidth: 140,
          },
        },
        '& .MuiIcon-root': {
          color: `${primaryContrast} !important`,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MainButton: {
      root: {
        backgroundColor: `${primary} !important`,
        color: `${primaryContrast} !important`,
        boxShadow: shadowButton,
        '&:hover': {
          backgroundColor: `${primaryDark} !important`,
          boxShadow: shadowButtonHover,
        },
      },
    },
    MuiButton: {
      containedPrimary: {
        backgroundColor: `${primary} !important`,
        color: `${primaryContrast} !important`,
        boxShadow: shadowButton,
        '&:hover': {
          backgroundColor: `${primaryDark} !important`,
          boxShadow: shadowButtonHover,
        },
      },
      outlined: {
        borderColor: buttonSecondaryBorder,
        backgroundColor: buttonSecondaryBg,
        color: textPrimary,
        '&:hover': {
          borderColor: borderMedium,
          backgroundColor: '#d5d5d5',
        },
      },
      root: {
        '&.custom-css-cta-button': {
          backgroundColor: primary,
          color: primaryContrast,
          boxShadow: shadowButton,
          '&:hover': {
            backgroundColor: primaryDark,
            boxShadow: shadowButtonHover,
          },
        },
      },
    },
    MuiModal: {
      root: {
        zIndex: 10001,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius,
        boxShadow: shadowPaper,
        border: 'none',
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0,
      },
      root: {
        '&.weha-amount-price-paper .MuiCard-root': {
          border: 'none !important',
          borderLeft: 'none !important',
          borderRight: 'none !important',
          borderBottom: 'none !important',
          borderTop: 'none !important',
          boxShadow: 'none !important',
        },
        '&.weha-amount-price-paper .MuiOutlinedInput-root fieldset': {
          border: 'none !important',
        },
        '&.weha-amount-price-paper .MuiDivider-root': {
          display: 'none',
        },
        // Net display: tan accent on price/caption typography
        '&.weha-amount-price-paper.weha-display-net .MuiTypography-body2': {
          color: `${subHeaderTan} !important`,
        },
        '&.weha-amount-price-paper.weha-display-net .MuiTypography-caption': {
          color: `${subHeaderTan} !important`,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius,
        backgroundColor: inputBackground,
        '& fieldset': {
          borderColor: borderDefault,
        },
        '&:hover fieldset': {
          borderColor: borderMedium,
        },
        '&.Mui-focused fieldset': {
          borderColor: primary,
          borderWidth: '1px',
        },
      },
    },
    MuiTypography: {
      body2: {
        color: textSecondary,
      },
    },
  });
};

export default function CustomThemeProvider({ children }) {
  return (
    <ConnectedThemeProvider customTheme={customTheme} withTheme={overrideTheme}>
      <CssBaseline />
      {children}
    </ConnectedThemeProvider>
  );
}

CustomThemeProvider.propTypes = {
  children: PropTypes.node,
};
